import React from 'react';
import { X } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';
import { ReviewEvidence } from './ReviewEvidence';
import { parseReview, preciseLocation, capacityLabel, capacityClaimValue, type ReviewEvidence as Evidence } from '../lib/review-evidence';

export interface DataCenterProperties {
  review?: Evidence | string | null;
  id: string;
  name: string;
  operator: string;
  status: string;
  capacity_mw: number | null;
  num_buildings: number | null;
  acreage: number | null;
  investment_usd: number | null;
  utility_provider: string | null;
  year_opened: number | null;
  year_announced: number | null;
  city: string | null;
  county: string | null;
  state: string;
  nearest_pipeline_name: string | null;
  nearest_pipeline_distance_mi: number | null;
  source: string | null;
  source_tag: string | null;
  notes: string | null;
  power_source: string | null;
  cooling_type: string | null;
  enrichment_status: string | null;
}

const POWER_SOURCE_LABELS: Record<string, string> = {
  grid: 'Grid (utility)',
  btm_gas: 'Behind-the-meter gas',
  btm_solar: 'Behind-the-meter solar',
  btm_nuclear: 'Behind-the-meter nuclear',
  mixed_grid_renewable: 'Grid + on-site renewable',
  mixed_grid_btm: 'Grid + on-site generation',
  unknown: 'Unknown',
};

const COOLING_LABELS: Record<string, string> = {
  air: 'Air-cooled',
  liquid: 'Liquid-cooled',
  evaporative: 'Evaporative (water)',
  closed_loop_dry: 'Closed-loop dry (zero-water)',
  mixed: 'Mixed',
  unknown: 'Unknown',
};

interface DataCenterDetailProps {
  dataCenter: DataCenterProperties;
  onClose: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  operational: '#0d9488',
  under_construction: '#d97706',
  announced: '#6366f1',
};

const STATUS_LABELS: Record<string, string> = {
  operational: 'Operational',
  under_construction: 'Under Construction',
  announced: 'Announced',
};

function formatInvestment(usd: number): string {
  return usd >= 1e9 ? `$${(usd / 1e9).toFixed(1)}B` : `$${Math.round(usd / 1e6)}M`;
}

function formatPipelineDistance(mi: number): string {
  return mi < 1 ? '< 1 mi' : `${mi.toFixed(1)} mi`;
}

export const DataCenterDetail: React.FC<DataCenterDetailProps> = ({ dataCenter, onClose }) => {
  const { theme } = useTheme();
  const dc = dataCenter;
  const statusColor = STATUS_COLORS[dc.status] || '#0d9488';
  const review = parseReview(dc.review);
  const statusLabel = review.operating_status?.replace(/_/g, ' ') || STATUS_LABELS[dc.status] || dc.status;
  const precise = preciseLocation(review);

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>{dc.name}</h2>
          <div style={{ marginTop: '6px' }}>
            <span style={{
              display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
              fontSize: '10px', fontWeight: 600, textTransform: 'uppercase',
              color: '#E4E6E8', background: statusColor,
            }}>
              {statusLabel}
            </span>
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
          color: theme.textSecondary, flexShrink: 0,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {dc.enrichment_status === 'unverified' && dc.source_tag === 'im3' && (
          <div style={{
            padding: '8px 10px', marginBottom: '14px',
            borderRadius: '4px',
            background: theme.backgroundSecondary,
            border: `1px solid ${theme.borderLight}`,
            fontSize: '11px', color: theme.textSecondary, lineHeight: 1.45,
          }}>
            <strong style={{ color: theme.text }}>Limited public data.</strong> This site comes from the
            PNNL data center atlas. Its location and current operating status need source verification.
          </div>
        )}

        <Section title="Overview">
          <Field label="Operator" value={dc.operator || null} />
          <Field label="Status as of" value={review.status_as_of || 'Not verified'} />
          {dc.capacity_mw != null && <Field label={capacityLabel(review)} value={`${Number(dc.capacity_mw).toLocaleString()} MW`} />}
          {dc.num_buildings && <Field label="Buildings" value={`${dc.num_buildings}`} />}
          {dc.acreage && <Field label="Acreage" value={`${Number(dc.acreage).toLocaleString()} acres`} />}
          {dc.investment_usd && <Field label="Investment" value={formatInvestment(Number(dc.investment_usd))} />}
          <Field label="Location" value={[dc.city, dc.county, dc.state].filter(Boolean).join(', ') || null} />
        </Section>

        <div style={{ fontSize: 12, lineHeight: 1.5, color: theme.textSecondary, marginBottom: 20 }}>
          {precise ? 'Site/address location verified in reviewed evidence.' : review.location_precision === 'approximate' ? 'Approximate location — distances are indicative.' : 'Location precision unverified — distances are indicative.'}
          {review.comparison_eligible === false ? ' Excluded from nearby campus comparisons pending review.' : ''}
        </div>
        {!!review.capacity_claims?.length && <Section title="Power and capacity claims">
          {review.capacity_claims.map((claim, i) => <div key={i} style={{ marginBottom: 14, fontSize: 12, lineHeight: 1.5 }}>
            <strong style={{ color: theme.text }}>{claim.label || claim.basis || 'Reported claim'} · {capacityClaimValue(claim)}</strong>
            <div style={{ color: theme.textSecondary }}>{[claim.basis, claim.scope, claim.status, claim.target_delivery ? `Target ${claim.target_delivery}` : null].filter(Boolean).join(' · ')}</div>
            {claim.source_url && <a href={claim.source_url} target="_blank" rel="noopener noreferrer" style={{ color: theme.text }}>Source</a>}
          </div>)}
        </Section>}

        {(dc.power_source || dc.utility_provider || dc.cooling_type) && (
          <Section title="Power & Cooling">
            <Field
              label="Power Source"
              value={dc.power_source ? POWER_SOURCE_LABELS[dc.power_source] || dc.power_source : null}
            />
            <Field label="Utility Provider" value={dc.utility_provider} />
            <Field
              label="Cooling"
              value={dc.cooling_type ? COOLING_LABELS[dc.cooling_type] || dc.cooling_type : null}
            />
          </Section>
        )}

        {(dc.year_opened || dc.year_announced || review.target_delivery) && (
          <Section title="Timeline">
            {dc.year_opened && <Field label={dc.status === 'operational' && !review.target_delivery ? 'Reported opening year' : 'Legacy opening year · unverified'} value={`${dc.year_opened}`} />}
            <Field label="Target delivery" value={review.target_delivery} />
            {dc.year_announced && <Field label="Year Announced" value={`${dc.year_announced}`} />}
          </Section>
        )}

        <Section title="Nearest Pipeline">
          {dc.nearest_pipeline_distance_mi != null ? (
            <>
              <Field label={precise ? "Mapped distance" : "Approximate distance"} value={formatPipelineDistance(Number(dc.nearest_pipeline_distance_mi))} />
              <Field label="Pipeline" value={dc.nearest_pipeline_name} />
            </>
          ) : (
            <div style={{ fontSize: '12px', color: theme.textTertiary, fontStyle: 'italic' }}>No pipeline data</div>
          )}
        </Section>

        <ReviewEvidence review={review} />

        {dc.notes && (
          <Section title={review.reviewed_at ? "Historical notes · may be superseded" : "Notes"}>
            <div style={{ fontSize: '12px', color: theme.textSecondary, fontStyle: 'italic', lineHeight: 1.5 }}>
              {dc.notes}
            </div>
          </Section>
        )}

        {dc.source && (
          <div style={{ fontSize: '10px', color: theme.textTertiary, marginTop: '12px' }}>
            Source: {dc.source_tag === 'im3' ? 'PNNL IM3 Data Center Atlas' : dc.source}
          </div>
        )}
      </div>
    </div>
  );
};
